import React from 'react';
import ReactECharts from 'echarts-for-react';
import { Dropdown, IDropdownOption, SpinButton, Spinner, SpinnerSize } from '@fluentui/react';
import { ArrowTrendingRegular, BriefcaseRegular, MoneyRegular, WarningRegular } from '@fluentui/react-icons';

import * as Functions from './FinancialPanel.Functions';
import { ChartColors, chartTooltip, useChartColors, useEchartsTheme } from '../chartTheme';

interface IProps {
    currency: string;
    rows: Functions.ProfitabilityRow[];
    order: Functions.ProfitabilityOrder;
    onOrderChange: (order: Functions.ProfitabilityOrder) => void;
    minMargin: number;
    onMinMarginChange: (value: number) => void;
    isLoading: boolean;
    loaded: { done: number; total: number };
    onProtocolClick: (protocolId: number) => void;
}

/** Margen sano / bajo el mínimo / pierde plata, con los colores de estado del tema activo. */
const healthColor = (colors: ChartColors): Record<Functions.MarginHealth, string> => ({
    good: colors.success,
    warn: colors.negative,
    bad: colors.danger
});
const HEALTH_LABEL: Record<Functions.MarginHealth, string> = {
    good: 'sobre el mínimo',
    warn: 'bajo el mínimo',
    bad: 'pierde plata'
};

const ORDER_OPTIONS: IDropdownOption[] = [
    { key: 'profit', text: 'Resultado: primero el que más drena' },
    { key: 'margin', text: 'Margen: primero el peor' },
    { key: 'roi', text: 'ROI: primero el peor' },
    { key: 'revenue', text: 'Ingresos: primero el más grande' }
];

/**
 * Bloque 2 — Rentabilidad. Contesta "¿qué protocolo deja plata y cuál la drena?".
 *
 * El orden por defecto pone arriba el que peor resultado da, no el más grande: un
 * protocolo que factura mucho y deja poco es justamente el que hay que renegociar, y
 * si la lista se ordena por facturación ese caso queda escondido entre los buenos.
 */
export const ProfitabilityBlockComponent: React.FC<IProps> = ({
    currency,
    rows,
    order,
    onOrderChange,
    minMargin,
    onMinMarginChange,
    isLoading,
    loaded,
    onProtocolClick
}) => {
    const money = (value: number) => Functions.formatMoney(value, currency);
    const compact = (value: number) => Functions.formatCompactMoney(value, currency);
    const colors = useChartColors();
    const chartTheme = useEchartsTheme();
    const HEALTH_COLOR = healthColor(colors);
    const totals = Functions.profitabilityTotals(rows);
    const ordered = Functions.sortProfitability(rows, order);
    const bySponsor = Functions.aggregateBySponsor(rows);

    const worst = ordered.filter((row) => row.profit < 0);

    const chartRows = ordered.slice(0, 15);
    const profitOption = {
        tooltip: {
            trigger: 'item',
            ...chartTooltip(colors),
            formatter: (params: any) => {
                const row = chartRows[params.dataIndex];
                return `<b>${row.protocolNumber}</b><br/>${row.sponsor}<br/>` +
                    `Ingresos: ${money(row.revenue)}<br/>Costos: ${money(row.cost)}<br/>` +
                    `<b>Resultado: ${money(row.profit)}</b><br/>Margen: ${Functions.formatPercent(row.margin)}`;
            }
        },
        grid: { left: 12, right: 80, top: 8, bottom: 8, containLabel: true },
        xAxis: { type: 'value', axisLabel: { fontSize: 10, formatter: (value: number) => compact(value) }, splitLine: { lineStyle: { color: colors.gridLine } } },
        yAxis: {
            type: 'category',
            data: chartRows.map((row) => row.protocolNumber).reverse(),
            axisTick: { show: false },
            axisLabel: { fontSize: 11, width: 120, overflow: 'truncate' }
        },
        series: [
            {
                type: 'bar',
                barMaxWidth: 22,
                data: chartRows
                    .slice()
                    .reverse()
                    .map((row) => ({
                        value: row.profit,
                        itemStyle: { color: HEALTH_COLOR[Functions.marginHealth(row.margin, minMargin)], borderRadius: [0, 4, 4, 0] }
                    })),
                label: { show: true, position: 'right', fontSize: 11, color: colors.textSecondary, formatter: (params: any) => compact(params.value) }
            }
        ]
    };

    if (isLoading && rows.length === 0) {
        return (
            <div className="cs-card">
                <div className="cs-card__toolbar">
                    <div className="fp-load">
                        <Spinner size={SpinnerSize.xSmall} />
                        <span>Calculando la rentabilidad: {loaded.done} de {loaded.total} protocolos</span>
                        <div className="cs-progress__bar fp-load__bar">
                            <div
                                className="cs-progress__fill"
                                style={{ width: `${loaded.total > 0 ? Math.round((loaded.done / loaded.total) * 100) : 0}%` }}
                            />
                        </div>
                    </div>
                </div>
                <div className="cs-loading cs-loading--compact">
                    <Spinner size={SpinnerSize.small} />
                    <span>Pidiendo el movimiento de cada protocolo…</span>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* ── Resultado del centro ── */}
            <div className="cs-card">
                <div className="cs-card__header">
                    <div className="cs-card__title">Resultado del centro</div>
                    <div className="cs-card__desc">
                        Ingresos menos egresos de cada protocolo, según la plata que efectivamente se movió en el período.
                        No incluye los costos fijos del centro que no estén cargados como egreso de un protocolo.
                    </div>
                </div>

                <div className="cs-kpi-grid">
                    <div className="cs-kpi-cell">
                        <div className="cs-kpi-cell__icon cs-kpi-cell__icon--green"><ArrowTrendingRegular /></div>
                        <div className="cs-kpi-cell__value">
                            <span className={totals.profit < 0 ? 'fp-amount--negative' : undefined}>{money(totals.profit)}</span>
                        </div>
                        <div className="cs-kpi-cell__label">Resultado</div>
                        <div className="cs-kpi-cell__desc">Ingresos menos costos de todos los protocolos</div>
                        <div className="cs-kpi-cell__sub">{money(totals.revenue)} de ingresos · {money(totals.cost)} de costos</div>
                    </div>
                    <div className="cs-kpi-cell">
                        <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><MoneyRegular /></div>
                        <div className="cs-kpi-cell__value">{Functions.formatPercent(totals.margin)}</div>
                        <div className="cs-kpi-cell__label">Margen del centro</div>
                        <div className="cs-kpi-cell__desc">Cuánto queda de cada peso que entra</div>
                        <div className="cs-kpi-cell__sub">
                            <span className={`fp-tag fp-tag--${Functions.marginHealth(totals.margin, minMargin)}`}>
                                {HEALTH_LABEL[Functions.marginHealth(totals.margin, minMargin)]}
                            </span>
                        </div>
                    </div>
                    <div className="cs-kpi-cell">
                        <div className="cs-kpi-cell__icon cs-kpi-cell__icon--orange"><WarningRegular /></div>
                        <div className="cs-kpi-cell__value">
                            <span className={totals.losingCount > 0 ? 'fp-amount--negative' : undefined}>{totals.losingCount}</span>
                        </div>
                        <div className="cs-kpi-cell__label">Protocolos que pierden plata</div>
                        <div className="cs-kpi-cell__desc">Gastan más de lo que ingresan en el período</div>
                        {totals.losingCount > 0 && (
                            <div className="cs-kpi-cell__sub">Se llevan {money(Math.abs(totals.losingAmount))}</div>
                        )}
                    </div>
                    <div className="cs-kpi-cell">
                        <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><BriefcaseRegular /></div>
                        <div className="cs-kpi-cell__value">{rows.length}</div>
                        <div className="cs-kpi-cell__label">Protocolos con movimiento</div>
                        <div className="cs-kpi-cell__desc">Los que tuvieron ingresos o egresos en el período</div>
                        {loaded.total > rows.length && (
                            <div className="cs-kpi-cell__sub">De {loaded.total} protocolos consultados</div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Protocolo por protocolo ── */}
            <div className="cs-card">
                <div className="cs-card__header">
                    <div className="cs-card__title">Protocolo por protocolo</div>
                    <div className="cs-card__desc">
                        Ordenado para que lo que hay que mirar quede arriba. Tocá un protocolo para abrirlo.
                    </div>
                </div>

                <div className="cs-card__toolbar">
                    <Dropdown
                        styles={{ root: { minWidth: 280 } }}
                        options={ORDER_OPTIONS}
                        selectedKey={order}
                        onChange={(_event, option) => option && onOrderChange(option.key as Functions.ProfitabilityOrder)}
                    />
                    <SpinButton
                        label="Margen mínimo (%)"
                        styles={{ root: { width: 170 } }}
                        value={String(minMargin)}
                        min={0}
                        max={100}
                        step={5}
                        onChange={(_event, value) => {
                            const parsed = Number((value || '').replace(/[^0-9.]/g, ''));
                            if (!Number.isNaN(parsed)) onMinMarginChange(parsed);
                        }}
                    />
                    <span className="cs-kpi-cell__desc">{rows.length} protocolos con movimiento en {currency}</span>
                </div>

                {rows.length === 0 ? (
                    <div className="cs-empty cs-empty--compact">
                        <BriefcaseRegular className="cs-empty__icon" />
                        <span>Ningún protocolo tuvo movimiento de dinero en {currency} en el período elegido.</span>
                        <span className="cs-empty__hint">Probá ampliando el rango de fechas o cambiando de moneda.</span>
                    </div>
                ) : (
                    <>
                        <div className="cs-card__chart-body">
                            <ReactECharts option={profitOption} style={{ height: Math.max(200, chartRows.length * 32) }} theme={chartTheme} opts={{ renderer: 'svg' }} />
                        </div>

                        <div className="cs-card__body">
                            <div className="cs-table-wrap">
                                <table className="cs-table">
                                    <thead>
                                        <tr>
                                            <th>Protocolo</th>
                                            <th>Sponsor</th>
                                            <th className="cs-table__amount">Ingresos</th>
                                            <th className="cs-table__amount">Costos</th>
                                            <th className="cs-table__amount">Resultado</th>
                                            <th className="cs-table__amount">Margen</th>
                                            <th className="cs-table__amount">ROI</th>
                                            <th className="cs-table__amount">% de ingresos</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ordered.map((row) => {
                                            const health = Functions.marginHealth(row.margin, minMargin);
                                            return (
                                                <tr
                                                    key={row.protocolId}
                                                    className="cs-table__interactive"
                                                    onClick={() => onProtocolClick(row.protocolId)}
                                                >
                                                    <td>
                                                        <div className="fp-entity">
                                                            <span className="fp-entity__name">{row.protocolNumber}</span>
                                                            <span className="fp-entity__meta">{row.name}</span>
                                                        </div>
                                                    </td>
                                                    <td>{row.sponsor}</td>
                                                    <td className="cs-table__amount">{money(row.revenue)}</td>
                                                    <td className="cs-table__amount">{money(row.cost)}</td>
                                                    <td className="cs-table__amount">
                                                        <span className={row.profit < 0 ? 'fp-amount--negative' : 'fp-amount--positive'}>
                                                            {money(row.profit)}
                                                        </span>
                                                    </td>
                                                    <td className="cs-table__amount">
                                                        <span className={`fp-tag fp-tag--${health}`}>{Functions.formatPercent(row.margin)}</span>
                                                    </td>
                                                    <td className="cs-table__amount">{Functions.formatPercent(row.roi)}</td>
                                                    <td className="cs-table__amount">{Functions.formatPercent(row.revenueShare)}</td>
                                                </tr>
                                            );
                                        })}
                                        <tr className="fp-total">
                                            <td>Total</td>
                                            <td>{bySponsor.length} sponsors</td>
                                            <td className="cs-table__amount">{money(totals.revenue)}</td>
                                            <td className="cs-table__amount">{money(totals.cost)}</td>
                                            <td className="cs-table__amount">
                                                <span className={totals.profit < 0 ? 'fp-amount--negative' : 'fp-amount--positive'}>
                                                    {money(totals.profit)}
                                                </span>
                                            </td>
                                            <td className="cs-table__amount">{Functions.formatPercent(totals.margin)}</td>
                                            <td className="cs-table__amount">—</td>
                                            <td className="cs-table__amount">100%</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {worst.length > 0 && (
                                <div className="fp-alert">
                                    <span className="fp-alert__icon" aria-hidden="true">!</span>
                                    <span>
                                        <strong>{worst.length} {worst.length === 1 ? 'protocolo gasta' : 'protocolos gastan'} más de lo que {worst.length === 1 ? 'ingresa' : 'ingresan'}</strong>
                                        {' '}({worst.slice(0, 3).map((row) => row.protocolNumber).join(', ')}
                                        {worst.length > 3 ? ` y ${worst.length - 3} más` : ''}).
                                        Entre todos se llevan {money(Math.abs(totals.losingAmount))} del resultado del centro.
                                    </span>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* ── Por sponsor ── */}
            {bySponsor.length > 0 && (
                <div className="cs-card">
                    <div className="cs-card__header">
                        <div className="cs-card__title">Por sponsor</div>
                        <div className="cs-card__desc">
                            El mismo resultado agrupado por quién paga. Sirve para la conversación de la próxima
                            negociación: con quién conviene seguir y con quién hay que revisar precios.
                        </div>
                    </div>
                    <div className="cs-card__body">
                        <div className="cs-table-wrap">
                            <table className="cs-table">
                                <thead>
                                    <tr>
                                        <th>Sponsor</th>
                                        <th className="cs-table__amount">Protocolos</th>
                                        <th className="cs-table__amount">Ingresos</th>
                                        <th className="cs-table__amount">Costos</th>
                                        <th className="cs-table__amount">Resultado</th>
                                        <th className="cs-table__amount">Margen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bySponsor.map((item) => (
                                        <tr key={item.sponsor}>
                                            <td><strong>{item.sponsor}</strong></td>
                                            <td className="cs-table__amount">{item.protocols}</td>
                                            <td className="cs-table__amount">{money(item.revenue)}</td>
                                            <td className="cs-table__amount">{money(item.cost)}</td>
                                            <td className="cs-table__amount">
                                                <span className={item.profit < 0 ? 'fp-amount--negative' : 'fp-amount--positive'}>
                                                    {money(item.profit)}
                                                </span>
                                            </td>
                                            <td className="cs-table__amount">
                                                <span className={`fp-tag fp-tag--${Functions.marginHealth(item.margin, minMargin)}`}>
                                                    {Functions.formatPercent(item.margin)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ProfitabilityBlockComponent;
